import React, { useState, useEffect } from 'react';
import { X, FileText, FileSpreadsheet, File } from 'lucide-react';
import exportReportCsv from '../../modules/reports/export/reportCsvExporter';
import exportReportExcel from '../../modules/reports/export/reportExcelExporter';
import exportReportPdf from '../../modules/reports/export/reportPdfExporter';
import { useDataTransfer } from '../../context/DataTransferContext';
import logoSrc from '../../assets/WM-Logo-CB-Transparent.png';

// "drawing_log" → "Custom Drawing Log"
const toDisplayName = (raw) =>
  'Custom ' + (raw || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const DownloadModal = ({ isOpen, onClose, data, columns, tableName }) => {
  const [filename, setFilename] = useState('');
  const [fileType, setFileType] = useState('csv');
  const { registerActivity, updateActivity, completeActivity, failActivity } = useDataTransfer();

  const displayName = toDisplayName(tableName);

  // Reset to defaults each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setFileType('csv');
      setFilename(displayName);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const handleDownload = async () => {
    const activityId = `download-${tableName}-${Date.now()}`;
    const exportName = filename.trim() || displayName;

    // Register in Data Transfer tracker, then close modal immediately
    registerActivity(activityId, {
      name: exportName,
      type: 'download',
      format: fileType,
      phase: 'Preparing…',
      pct: 0
    });

    onClose();

    // Capture state in closure before the async work
    const config = { reportName: exportName };
    const exportCols = columns.map(c => ({ field: c.key, label: c.label }));
    const exportData = data;

    try {
      if (fileType === 'csv') {
        await exportReportCsv({
          config,
          columns: exportCols,
          data: exportData,
          isCancelled: () => false,
          onProgress: (msg) => updateActivity(activityId, { phase: msg || 'Generating CSV…' })
        });
      } else if (fileType === 'excel') {
        await exportReportExcel({
          config,
          columns: exportCols,
          data: exportData,
          isCancelled: () => false,
          onProgress: (msg) => updateActivity(activityId, { phase: msg || 'Building Excel…' })
        });
      } else if (fileType === 'pdf') {
        const colCount = exportCols.length || 1;
        const colWidth = Math.floor(100 / colCount);
        const columnWidths = Object.fromEntries(exportCols.map(c => [c.field, colWidth]));
        await exportReportPdf({
          config,
          columns: exportCols,
          columnWidths,
          data: exportData,
          parameters: {},
          logoSrc,
          rowsPerPage: 40,
          isCancelled: () => false,
          onProgress: (msg) => updateActivity(activityId, { phase: msg || 'Rendering PDF…' }),
          onPhase: () => {},
          onWorkerReady: () => {}
        });
      }
      completeActivity(activityId);
    } catch {
      failActivity(activityId);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content download-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Download Data</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label>Filename</label>
            <input
              type="text"
              value={filename}
              onChange={e => setFilename(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>File Type</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="fileType"
                  value="csv"
                  checked={fileType === 'csv'}
                  onChange={e => setFileType(e.target.value)}
                />
                <FileText size={16} />
                <span>CSV</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="fileType"
                  value="excel"
                  checked={fileType === 'excel'}
                  onChange={e => setFileType(e.target.value)}
                />
                <FileSpreadsheet size={16} />
                <span>Excel</span>
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="fileType"
                  value="pdf"
                  checked={fileType === 'pdf'}
                  onChange={e => setFileType(e.target.value)}
                />
                <File size={16} />
                <span>PDF</span>
              </label>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleDownload}>
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;
